import { css } from "@emotion/react";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import React, { useMemo } from "react";
import cn from "classnames";
import { TypedMoney } from "@swo/storefront";
import { ContentfulImage } from "../_shared-components/image-related/ContentfulImage";
import { NoImageSvg } from "../../assets";
import { CSS_MQ } from "../../styles/breakpoints";
import { LinkOrSpan } from "./LinkOrSpan";
import type { LinkProps } from "react-router";
import type { AssetType, EnrichedSwarovskiProductVariantDiscountedPriceFrontend } from "@swo/shared";
import type { ShowDiscountablePriceF, ShowDiscountablePriceStrF } from "../../global/PageContextType";
import { AbsoluteDiscountBadge } from "../../layouts/product-detail-page-layout/PDPAboveTheFoldMainImage";

type ProductViewProps = {
  linkTo: LinkProps["to"] | undefined;
  background: "transparent" | "grey";
  showDiscountablePrice: ShowDiscountablePriceF;
  image: AssetType | undefined;
  headline: string;
  price: TypedMoney | undefined;
  discountedPrice: EnrichedSwarovskiProductVariantDiscountedPriceFrontend | undefined;
  isDiscounted: boolean;
};

export const ProductView: React.FC<ProductViewProps> = ({
  linkTo,
  background,
  showDiscountablePrice,
  image,
  headline,
  price,
  discountedPrice,
  isDiscounted,
}) => {
  const ImageWithFallback = image ? (
    <ContentfulImage
      assetId={image.sys.id}
      contentfulLink={{
        url: image.url,
        noTransparentBackground: false,
        requestedWidth: 300,
      }}
      higherDPI={[2]}
      css={css`
        width: 100%;
        height: 100%;
        object-fit: contain;
      `}
      alt={image.description || image.title || undefined}
      attachPictureStyles={css`
        width: 100%;
        height: 100%;
      `}
      lazyLoaded={"lazy"}
    />
  ) : (
    <NoImageSvg
      css={css`
        width: 80px;
      `}
      strokeWidth={1}
    />
  );

  return (
    <div
      css={wrapperStyles}
      className={cn(
        { "without-link": !linkTo },
        background === "grey" ? "variant-with-background" : undefined,
        "js-product-carousel-element",
        "u-relative",
      )}
    >
      <LinkOrSpan to={linkTo} css={styles}>
        {ImageWithFallback}
      </LinkOrSpan>

      <span css={headlineWrapperStyles}>
        <LinkOrSpan to={linkTo} css={linkStyles}>
          {headline}
        </LinkOrSpan>
      </span>
      {price && (
        <span css={priceWrapperStyles}>
          {showDiscountablePrice({
            price,
            discountedPrice: discountedPrice || "no-discount-provided",
          })}
        </span>
      )}

      <AbsoluteDiscountBadge isDiscounted={isDiscounted} />
    </div>
  );
};

const linkStyles = css`
  text-decoration: none;
  color: inherit;
`;

const styles = [
  linkStyles,
  css`
    display: flex;
    justify-content: center;
    align-items: center;

    .variant-with-background & {
      aspect-ratio: 1/1;

      background-color: ${CSS_COLORS.GreenWhite};

      position: relative;
      picture {
        position: absolute;
        padding: 20%;
      }
    }
  `,
];

const wrapperStyles = css`
  display: grid;
  grid-template-rows: 180px auto auto;

  padding: 8px;
  ${CSS_MQ.atLeastMd} {
    padding: 20px;
  }

  margin-left: 2px;
  margin-right: 2px;

  --h: 70px;
  --a1: 20px;
  --a2: 0px;

  height: 100%;

  &.without-link {
    --a2: 0px;
  }

  &.variant-with-background {
    grid-template-rows: auto auto auto;
    margin: 0;
    padding: 0;
  }

  --a: calc(var(--a1) + var(--a2));
  --ha: calc(var(--h) - var(--a));
`;

const headlineWrapperStyles = [
  css`
    justify-self: flex-start;

    padding-top: 20px;
    ${CSS_MQ.atLeastMd} {
      padding-top: 60px;
    }

    display: block;

    .without-link & {
      color: ${CSS_COLORS.Black};
    }

    .variant-with-background & {
      padding-top: 12px;

      ${CSS_MQ.atLeastMd} {
        padding-top: 30px;
      }
    }
  `,
  FONTS.SwarovskiFutura500,
  fontColor.black,
  fontSize.x2,
];

const priceWrapperStyles = [
  css`
    margin-top: 6px;
  `,
  fontColor.black,
  FONTS.SwarovskiFutura300,
  fontSize.x2,
];
